import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setFormData, setFormErrors, clearFormErrors } from 'store/slices/valuePackSlice';
import { DEFAULT_FORM_VALUES } from 'config/valuePackFormConfig';
import { getValidationSchemaByStep } from 'schemas/valuePackSchema';

export const useValuePackForm = (currentStep = 0, isEdit, validationTriggered = false) => {
  const dispatch = useDispatch();
  const { 
    formData, 
    loading,
    submitting, 
    error,
    isEditMode : reduxEditMode
  } = useSelector((state) => state.valuePack);
  
  const actualEditMode = isEdit !== undefined ? isEdit : reduxEditMode;
  const validationSchema = getValidationSchemaByStep(currentStep, actualEditMode);

  const form = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    setValue,
    watch,
    reset,
    clearErrors,
    setError,
    formState: { errors, isValid, isDirty, dirtyFields }
  } = form;

  const watchedValues = watch();

  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      const resetData = {
        ...DEFAULT_FORM_VALUES,
        ...formData
      };
      reset(resetData);
    }
  }, [formData, reset]);

  useEffect(() => {
    const subscription = watch((data, { name, type }) => {
      if (type === 'change' && name) {
        // Only sync if the data has actually changed
        if (JSON.stringify(data) !== JSON.stringify(formData)) {
          dispatch(setFormData(data));
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, dispatch, formData]);

  useEffect(() => {
    const hasErrors = Object.keys(errors).length > 0;
    
    if (hasErrors) {
      dispatch(setFormErrors(errors));
    } else {
      dispatch(clearFormErrors());
    }
  }, [errors, dispatch]);

  useEffect(() => {
    if (validationTriggered) {
      const validateForm = async () => {
        await trigger();
      };
      validateForm();
    }
  }, [validationTriggered, trigger, currentStep]);

  const validateCurrentStep = useCallback(async (stepData = null) => {
    try {
      const stepValid = await trigger(undefined, { shouldFocus: true });
      if (!stepValid) {
        // Log each invalid field
        if (errors && Object.keys(errors).length > 0) {
          Object.entries(errors).forEach(([fieldName, error]) => {});
        }
      }
      
      return stepValid;
    } catch (error) {
      console.error('🚨 ERROR in validateCurrentStep:', error);
      throw error; 
    }
  }, [trigger, errors, currentStep, reduxEditMode]);

  const getFieldValue = useCallback((fieldName) => {
    const value = getValues(fieldName);
    return value !== undefined ? value : formData[fieldName];
  }, [getValues, formData]);
  
  const setFieldValue = useCallback((fieldName, value) => {
    const commonOptions = { 
      shouldValidate: true, 
      shouldDirty: true,
      shouldTouch: true
    };

    // Image files ke liye simple handling
    if (fieldName === 'imageFile' || fieldName === 'bannerImageFile') {
      setValue(fieldName, value, { 
        ...commonOptions,
        shouldValidate: false
      });
    } 
    else if (fieldName === 'categories') {
      setValue(fieldName, value, commonOptions);
    } 
    else if (fieldName === 'durationId' || fieldName === 'countryId' || 
            fieldName === 'selectedDuration' || fieldName === 'selectedCountry') {
      // For duration and country, store the full object
      setValue(fieldName, value, commonOptions);
    }
    else {
      // Regular fields
      setValue(fieldName, value, commonOptions);
    }
    
    // Redux sync karein
    const currentData = getValues();
    dispatch(setFormData({ ...currentData, [fieldName]: value }));
  }, [setValue, getValues, dispatch]);

  const getAllValues = useCallback(() => {
    const currentValues = getValues();
    const watchedValues = watch();
  
    const mergedData = {
      ...formData,
      ...currentValues
    };
  
    return mergedData;
  }, [getValues, formData, watch]);

  const hasFieldError = useCallback((fieldName) => {
    return !!errors[fieldName];
  }, [errors]);

  const getFieldError = useCallback((fieldName) => {
    const fieldError = errors[fieldName];
    if (!fieldError) return '';
    
    if (typeof fieldError.message === 'string') {
      return fieldError.message;
    }
    
    if (fieldError.message) {
      return fieldError.message;
    }
    
    return fieldError.toString();
  }, [errors]);

  const resetForm = useCallback(() => {
    reset(DEFAULT_FORM_VALUES);
    dispatch(setFormData(DEFAULT_FORM_VALUES));
    clearErrors();
    dispatch(clearFormErrors());
  }, [reset, dispatch, clearErrors]);

  const onSubmit = useCallback((callback) => {
    return handleSubmit(async (data) => {
      try {
        const mergedData = {
          ...formData,
          ...data
        };
        await callback(mergedData);
      } catch (error) {
        console.error('Form submission error:', error);
      }
    });
  }, [handleSubmit, formData]);

  const setFieldError = useCallback((fieldName, message) => {
    setError(fieldName, {
      type: 'manual',
      message: message
    });
  }, [setError]);

  const clearFieldError = useCallback((fieldName) => {
    clearErrors(fieldName);
  }, [clearErrors]);

  const validateField = useCallback(async (fieldName) => {
    try {
      const result = await trigger(fieldName);
      return result;
    } catch (error) {
      console.error(`Validation error for field ${fieldName}:`, error);
      return false;
    }
  }, [trigger]);

  const hasChanges = useCallback(() => {
    return isDirty || Object.keys(dirtyFields).length > 0;
  }, [isDirty, dirtyFields]);

  const getFormState = useCallback(() => {
    return {
      isValid,
      isDirty,
      hasErrors: Object.keys(errors).length > 0,
      errorCount: Object.keys(errors).length,
      dirtyFieldCount: Object.keys(dirtyFields).length
    };
  }, [isValid, isDirty, errors, dirtyFields]);

  return {
    // Form methods
    control,
    handleSubmit: onSubmit,
    trigger: validateCurrentStep,
    getAllValues,
    setValue: setFieldValue,
    watch,
    reset: resetForm,
    
    // Form state
    errors,
    isValid,
    isDirty,
    dirtyFields,
    watchedValues,
    
    // Custom utilities
    getFieldValue,
    setFieldValue,
    hasFieldError,
    getFieldError,
    setFieldError,
    clearFieldError,
    validateField,
    hasChanges,
    getFormState,
    
    // Redux state - Simplified loading states
    formData,
    loading: loading || submitting,
    error,
    reduxEditMode,
    
    // Form validation
    validateCurrentStep
  };
};

export const useStepValidation = (stepIndex, isEdit = false) => {
  const dispatch = useDispatch();
  const { formData, isEditMode: reduxEditMode } = useSelector((state) => state.valuePack);
  
  const actualEditMode = isEdit !== undefined ? isEdit : reduxEditMode;
  
  const validationSchema = getValidationSchemaByStep(stepIndex, actualEditMode);
  
  const validateStep = useCallback(async (data = formData) => {
    try {
      await validationSchema.validate(data, { abortEarly: false });
      return { isValid: true, errors: {} };
    } catch (validationError) {
      const errors = {};
      if (validationError.inner) {
        validationError.inner.forEach(error => {
          errors[error.path] = error.message;
        });
      } else {
        errors[validationError.path || 'general'] = validationError.message;
      }
      dispatch(setFormErrors(errors));
      return { isValid: false, errors };
    }
  }, [validationSchema, formData, dispatch]);

  const validateStepField = useCallback(async (fieldName, value, data = formData) => {
    try {
      const fieldSchema = validationSchema.fields[fieldName];
      if (fieldSchema) {
        await fieldSchema.validate(value);
        return { isValid: true, error: null };
      }
      return { isValid: true, error: null };
    } catch (error) {
      return { isValid: false, error: error.message };
    }
  }, [validationSchema, formData]);
  
  return { 
    validateStep, 
    validateStepField,
    validationSchema 
  };
};
